import type { ScaleDegree } from './solfegeAndScaleDegrees';

export const chordQualityNames = ['M', 'M7', 'm', 'm7', 'A', 'o', 'o7', 'o/7', '7'] as const;
export type ChordQualityName = typeof chordQualityNames[number];

export const inversionNumbers = [0, 1, 2, 3] as const;
export type InversionNumber = typeof inversionNumbers[number];

/**
 * All acceptable figures for constructing a chord or roman numeral
 */
export const validFigures = [
  '',
  '53',
  '63',
  '6',
  '64',
  '7',
  '753',
  '75',
  '73',
  '653',
  '65',
  '643',
  '43',
  '642',
  '42',
] as const;

/**
 * All acceptable figures for constructing a chord or roman numeral
 */
export type ValidFigure = typeof validFigures[number];

/**
 * Normalized figures for a chord or roman numeral. If a more longhand
 * version of any of these is used in constructing a chord/roman numeral,
 * the Chord or RomanNumeral class will convert the figures to one of these.
 */
export const figures = ['', '63', '64', '7', '65', '43', '42'] as const;

/**
 * Normalized figures for a chord or roman numeral. If a more longhand
 * version of any of these is used in constructing a chord/roman numeral,
 * the Chord or RomanNumeral class will convert the figures to one of these.
 */
export type Figure = typeof figures[number];

interface ChordQualityEntry {
  major: {
    triads: ChordQualityName[];
    seventhChords: ChordQualityName[];
  };
  minor: {
    triads: ChordQualityName[];
    seventhChords: ChordQualityName[];
  };
}

export const chordQualitiesByScaleDegree: Record<ScaleDegree, ChordQualityEntry> = {
  1: {
    major: {
      triads: ['M'],
      seventhChords: ['M7'],
    },
    minor: {
      triads: ['m'],
      seventhChords: ['m7'],
    },
  },
  2: {
    major: {
      triads: ['m'],
      seventhChords: ['m7'],
    },
    minor: {
      triads: ['o'],
      seventhChords: ['o/7'],
    },
  },
  3: {
    major: {
      triads: ['m'],
      seventhChords: ['m7'],
    },
    minor: {
      triads: ['M'],
      seventhChords: [],
    },
  },
  4: {
    major: {
      triads: ['M'],
      seventhChords: ['M7'],
    },
    minor: {
      triads: ['m'],
      seventhChords: ['m7'],
    },
  },
  5: {
    major: {
      triads: ['M', 'A'],
      seventhChords: ['7'],
    },
    minor: {
      triads: ['m', 'M'],
      seventhChords: ['7'],
    },
  },
  6: {
    major: {
      triads: ['m'],
      seventhChords: ['m7'],
    },
    minor: {
      triads: ['M'],
      seventhChords: [],
    },
  },
  M6: {
    major: {
      triads: [],
      seventhChords: [],
    },
    minor: {
      triads: [],
      seventhChords: [],
    },
  },
  7: {
    major: {
      triads: ['o'],
      seventhChords: ['o/7'],
    },
    minor: {
      triads: ['M'],
      seventhChords: [],
    },
  },
  M7: {
    major: {
      triads: [],
      seventhChords: [],
    },
    minor: {
      triads: ['o'],
      seventhChords: ['o7'],
    },
  },
  8: {
    major: {
      triads: [],
      seventhChords: [],
    },
    minor: {
      triads: [],
      seventhChords: [],
    },
  },
};

// Chords as half steps from a starting pitch:
export const chordHalfStepsFromRoot = {
  M: [0, 4, 7],
  m: [0, 3, 7],
  o: [0, 3, 6],
  A: [0, 4, 8],
  '7': [0, 4, 7, 10],
  M7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  o7: [0, 3, 6, 9],
  'o/7': [0, 3, 6, 10],
} as const;
